import { useState, useEffect, useRef } from 'react';
import { peritosService } from '../services/peritosService';

/**
 * Selector de perito con búsqueda.
 * Props:
 *   value:    { perito: string, peritoMovil: string }
 *   onChange: ({ perito, peritoMovil }) => void
 *   disabled: boolean
 */
export default function PeritoSelector({ value, onChange, disabled }) {
  const [buscando, setBuscando] = useState(false);
  const [query, setQuery]       = useState('');
  const [resultados, setResultados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  const tienePerito = !!value?.perito;

  // Abrir búsqueda y enfocar input
  const abrirBusqueda = () => {
    setBuscando(true);
    setQuery('');
    setResultados([]);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const limpiar = () => {
    onChange({ perito: '', peritoMovil: '' });
    setBuscando(false);
    setQuery('');
    setResultados([]);
  };

  // Búsqueda con debounce
  useEffect(() => {
    if (!buscando) return;
    clearTimeout(timerRef.current);
    if (!query.trim()) { setResultados([]); return; }
    timerRef.current = setTimeout(async () => {
      setCargando(true);
      try {
        const r = await peritosService.listar(query.trim(), 0, 8);
        setResultados(r.data?.content || []);
      } catch {
        setResultados([]);
      } finally {
        setCargando(false);
      }
    }, 250);
    return () => clearTimeout(timerRef.current);
  }, [query, buscando]);

  const seleccionar = (p) => {
    const nombre = [p.nombre, p.apellidos].filter(Boolean).join(' ');
    const movil  = p.movil || p.telefono || '';
    onChange({ perito: nombre, peritoMovil: movil });
    setBuscando(false);
    setQuery('');
    setResultados([]);
  };

  // Cerrar dropdown al hacer click fuera
  const contenedorRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target)) {
        setBuscando(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={contenedorRef} className="col-span-2">
      <div className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Perito</h3>
          {!disabled && !buscando && (
            <div className="flex items-center gap-2">
              {tienePerito && (
                <button type="button" onClick={abrirBusqueda}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                  Cambiar
                </button>
              )}
              {tienePerito && (
                <button type="button" onClick={limpiar}
                  className="text-xs text-red-400 hover:text-red-600">
                  Quitar
                </button>
              )}
            </div>
          )}
        </div>

        {/* Perito seleccionado */}
        {tienePerito && !buscando && (
          <div className="flex items-center gap-3 bg-white rounded-lg px-3 py-2.5 border border-gray-200">
            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
              <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{value.perito}</p>
              {value.peritoMovil && (
                <a href={`tel:${value.peritoMovil}`}
                  className="text-xs text-primary-600 hover:underline"
                  onClick={e => e.stopPropagation()}>
                  {value.peritoMovil}
                </a>
              )}
            </div>
          </div>
        )}

        {/* Sin perito */}
        {!tienePerito && !buscando && (
          <button type="button" onClick={!disabled ? abrirBusqueda : undefined}
            disabled={disabled}
            className="w-full flex items-center gap-2 border-2 border-dashed border-gray-300
                       rounded-lg px-3 py-2 text-sm text-gray-400 hover:border-primary-400
                       hover:text-primary-600 transition disabled:opacity-50 disabled:cursor-not-allowed">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
            </svg>
            Seleccionar perito
          </button>
        )}

        {/* Buscador */}
        {buscando && (
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Nombre, apellidos..."
              className="w-full border border-primary-400 rounded-lg px-3 py-2 pr-9 text-sm
                         focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
            />
            {cargando
              ? <div className="absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"/>
              : (
                <button type="button" onClick={() => setBuscando(false)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              )
            }

            {/* Dropdown resultados */}
            {resultados.length > 0 && (
              <div className="absolute z-30 w-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                {resultados.map(p => {
                  const nombre = [p.nombre, p.apellidos].filter(Boolean).join(' ');
                  return (
                    <button key={p.id} type="button" onMouseDown={() => seleccionar(p)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-primary-50 text-left transition">
                      <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                        <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{nombre}</p>
                        {(p.movil || p.telefono) && (
                          <p className="text-xs text-gray-500">{p.movil || p.telefono}</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {query.trim() && !cargando && resultados.length === 0 && (
              <div className="absolute z-30 w-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 px-3 py-3">
                <p className="text-sm text-gray-400 text-center">Sin resultados para "{query}"</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
